<template>
  <div class="project-usp-send-handbook">
    <PageTitle hideMarginLeft>
      Отправка оперфакта, сметы и договора в УСП
    </PageTitle>
    <p class="handbook-desc">
      Выберите проект УСП и управляйте разрешением на отправку оперфакта, сметы и договоров в УСП.
    </p>

    <div class="handbook-card handbook-card--filters">
      <div class="filters">
        <div class="filter-item">
          <CustomSelect
            label="Проект УСП"
            placeholder="Выберите проект УСП"
            :required="true"
            :options="sbsProjectsOptions"
            option-value-key="sbs_project_id"
            option-label-key="sbs_project_name"
            :value="sbsProjectId"
            :loading="isSbsProjectsLoading"
            :useInput="!sbsProjectId"
            @change="onSbsProjectChange"
          />
        </div>
        <div class="filter-item filter-item--actions">
          <q-btn
            outline
            color="primary"
            icon="refresh"
            label="Обновить"
            no-caps
            :loading="isFetching"
            :disable="!sbsProjectId"
            class="refresh-btn"
            @click="loadProjects"
          />
        </div>
      </div>
    </div>

    <q-linear-progress
      v-if="isSbsProjectsLoading || isFetching"
      indeterminate
      class="handbook-progress"
    />

    <div v-else-if="!sbsProjectId" class="handbook-card handbook-card--empty">
      <q-icon name="apartment" size="48px" color="grey-5" />
      <div class="empty-title">Выберите проект УСП</div>
      <div class="empty-text">После выбора здесь появится статус отправки в УСП</div>
    </div>

    <div v-else class="handbook-card handbook-card--table">
      <CustomTable
        title="Проекты УСП"
        :rowsData="rowsData"
        :columns="projectUspSendColumns"
        :loading="isFetching"
        :show-search="false"
        :custom-body="true"
        :small="true"
      >
        <template #customBody="props">
          <q-tr>
            <custom-table-cell align="center" vertical-align="center">
              <span class="project-id">{{ props.row.sbs_project_id }}</span>
            </custom-table-cell>
            <custom-table-cell align="left" vertical-align="center">
              {{ props.row.sbs_project_name || selectedSbsProjectName || '—' }}
            </custom-table-cell>
            <custom-table-cell align="center" vertical-align="center">
              <div class="toggle-cell-wrapper">
                <q-spinner
                  v-if="toggleLoading[props.row.sbs_project_id]"
                  size="18px"
                  color="primary"
                />
                <q-chip
                  v-else
                  :color="isUspSendEnabled(props.row) ? 'green-2' : 'red-2'"
                  :text-color="isUspSendEnabled(props.row) ? 'green-10' : 'red-10'"
                  square
                  dense
                >
                  {{ isUspSendEnabled(props.row) ? 'Включена' : 'Выключена' }}
                </q-chip>
              </div>
            </custom-table-cell>
            <custom-table-cell align="center" vertical-align="center">
              <div class="row-actions">
                <q-btn
                  dense
                  outline
                  no-caps
                  size="sm"
                  :color="isUspSendEnabled(props.row) ? 'negative' : 'positive'"
                  :label="isUspSendEnabled(props.row) ? 'Выключить' : 'Включить'"
                  class="row-action-btn"
                  :loading="toggleLoading[props.row.sbs_project_id]"
                  :disable="toggleLoading[props.row.sbs_project_id]"
                  @click="onToggleClick(props.row)"
                />
                <q-btn
                  flat
                  round
                  dense
                  icon="history"
                  color="grey-7"
                  @click="openHistory(props.row)"
                >
                  <q-tooltip>Журнал изменений</q-tooltip>
                </q-btn>
              </div>
            </custom-table-cell>
          </q-tr>
        </template>
      </CustomTable>
      <NotFoundText v-if="!isFetching && rowsData.length === 0" />
    </div>

    <q-dialog v-model="historyDialog.show" persistent>
      <q-card class="history-dialog">
        <q-card-section class="history-dialog__header">
          <div class="text-h6">Журнал отправки в УСП</div>
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section class="history-dialog__body">
          <div class="history-project-info">
            <div class="history-project-info__item">
              <span class="history-project-info__label">ID</span>
              <span class="history-project-info__value">#{{ historyDialog.sbsProjectId }}</span>
            </div>
            <div class="history-project-info__item">
              <span class="history-project-info__label">Проект УСП</span>
              <span class="history-project-info__value">{{ historyDialog.sbsProjectName || '—' }}</span>
            </div>
          </div>

          <q-linear-progress v-if="historyDialog.loading" indeterminate class="q-mt-md" />

          <div v-else-if="!historyDialog.rows.length" class="history-empty">
            Изменений нет
          </div>

          <q-table
            v-else
            flat
            bordered
            dense
            class="history-table q-mt-md"
            :rows="historyDialog.rows"
            :columns="historyColumns"
            row-key="sbs_project_usp_send_history_id"
            :pagination="{ rowsPerPage: 10 }"
            :rows-per-page-options="[10, 20, 50]"
          />
        </q-card-section>

        <q-card-actions align="right" class="history-dialog__footer">
          <q-btn flat label="Закрыть" color="grey-7" no-caps v-close-popup />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed } from "vue";
import PageTitle from "@/components/common/PageTitle.vue";
import CustomSelect from "@/components/form/CustomSelect";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import NotFoundText from "@/components/common/NotFoundText";
import { useQuery } from "@tanstack/vue-query";
import { useAppStore } from "@/services/store/app";
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import { MODAL_NAMES } from "@/constants";
import {
  getSbsProjects,
  getHandbookProjects,
  setUspSendEnabled,
  getUspSendHistory,
  projectUspSendColumns,
} from "./services";

const appStore = useAppStore();
const { showSucceedSnackbar } = useCustomSnackbar();
const { showModal, closeModal } = appStore;

const sbsProjectId = ref(null);
const rowsData = ref([]);
const isFetching = ref(false);
const toggleLoading = reactive({});

const historyDialog = reactive({
  show: false,
  loading: false,
  sbsProjectId: null,
  sbsProjectName: "",
  rows: [],
});

const historyColumns = [
  { name: "changed_at", label: "Дата", field: "changed_at", align: "left" },
  { name: "employee_fio", label: "Сотрудник", field: "employee_fio", align: "left" },
  {
    name: "is_usp_send_enabled",
    label: "Состояние",
    field: "is_usp_send_enabled",
    align: "center",
    format: (val) => (val ? "Включено" : "Выключено"),
  },
];

const { data: sbsProjectsData, isLoading: isSbsProjectsLoading } = useQuery({
  queryKey: ["handbookProjectUspSendSbsProjects"],
  queryFn: getSbsProjects,
  refetchOnWindowFocus: false,
});

const sbsProjectsOptions = ref([]);

watch(
  sbsProjectsData,
  (val) => {
    sbsProjectsOptions.value = Array.isArray(val) ? val : [];
  },
  { immediate: true }
);

const selectedSbsProjectName = computed(() => {
  const row = sbsProjectsOptions.value.find((p) => p.sbs_project_id === sbsProjectId.value);
  return row?.sbs_project_name || row?.naimenovanie || "";
});

function isUspSendEnabled(row) {
  return row?.is_usp_send_enabled !== false;
}

async function loadProjects() {
  if (!sbsProjectId.value) {
    rowsData.value = [];
    return;
  }
  isFetching.value = true;
  const res = await getHandbookProjects({ sbs_project_id: sbsProjectId.value });
  rowsData.value = res || [];
  isFetching.value = false;
}

function onSbsProjectChange(value) {
  sbsProjectId.value = value ?? null;
  loadProjects();
}

function onToggleClick(row) {
  const isEnabled = isUspSendEnabled(row);
  const newValue = !isEnabled;
  const actionText = newValue ? "включить" : "выключить";
  const title = newValue ? "Включение отправки в УСП" : "Выключение отправки в УСП";
  const btnText = newValue ? "Включить" : "Выключить";
  const color = newValue ? "#4caf50" : "#f44336";

  showModal(MODAL_NAMES.CONFIRM, {
    title,
    message: `<div style="font-size: 14px; line-height: 1.7;">
      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
        <span style="font-size: 20px; color: ${color};">●</span>
        <span>Вы собираетесь <b>${actionText}</b> отправку оперфакта, сметы и договоров в УСП</span>
      </div>
      <div style="background: #f5f7fa; border-radius: 8px; padding: 12px 16px;">
        <div><b>ID:</b> #${row.sbs_project_id}</div>
        <div><b>Проект УСП:</b> ${row.sbs_project_name || selectedSbsProjectName.value || "—"}</div>
        <div><b>Текущее состояние:</b> ${isEnabled ? "отправка включена" : "отправка выключена"}</div>
      </div>
    </div>`,
    isHtml: true,
    btnText,
    cancelBtnText: "Отмена",
    onSubmit: async () => {
      closeModal();
      toggleLoading[row.sbs_project_id] = true;
      await setUspSendEnabled({
        sbs_project_id: row.sbs_project_id,
        is_usp_send_enabled: newValue,
      });
      toggleLoading[row.sbs_project_id] = false;
      await loadProjects();
      showSucceedSnackbar();
    },
  });
}

async function openHistory(row) {
  historyDialog.sbsProjectId = row.sbs_project_id;
  historyDialog.sbsProjectName = row.sbs_project_name || selectedSbsProjectName.value || "";
  historyDialog.show = true;
  historyDialog.loading = true;
  historyDialog.rows = await getUspSendHistory(row.sbs_project_id);
  historyDialog.loading = false;
}
</script>

<style lang="scss" scoped>
.project-usp-send-handbook {
  min-height: 400px;
}

.handbook-desc {
  margin-top: 8px;
  margin-bottom: 20px;
  max-width: 720px;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
}

.handbook-card {
  background: #fff;
  border: 1px solid #e8edf2;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.04);
  margin-bottom: 16px;
}

.handbook-card--filters {
  padding: 20px;
}

.handbook-card--table {
  padding: 8px 8px 0;
  overflow: hidden;
}

.handbook-card--empty {
  padding: 48px 24px;
  text-align: center;
}

.handbook-progress {
  margin-bottom: 16px;
  border-radius: 4px;
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}

.filter-item {
  flex: 1 1 280px;
  min-width: 240px;
  max-width: 360px;
}

.filter-item--actions {
  flex: 0 0 auto;
  min-width: auto;
  max-width: none;
}

.refresh-btn {
  height: 40px;
}

.empty-title {
  margin-top: 12px;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
}

.empty-text {
  margin-top: 6px;
  font-size: 13px;
  color: #9ca3af;
}

.project-id {
  font-weight: 600;
  color: #1976d2;
}

.row-actions {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.row-action-btn {
  min-width: 96px;
}

.toggle-cell-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 28px;
}

.history-dialog {
  min-width: 560px;
  max-width: 90vw;
  border-radius: 12px;
}

.history-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
}

.history-dialog__body {
  padding-top: 0;
}

.history-dialog__footer {
  padding: 12px 20px 16px;
}

.history-project-info {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding: 14px 16px;
  background: #f5f7fa;
  border-radius: 10px;
  border: 1px solid #e8edf2;
}

.history-project-info__item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.history-project-info__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #9ca3af;
}

.history-project-info__value {
  font-size: 13px;
  color: #374151;
  line-height: 1.4;
  word-break: break-word;
}

.history-empty {
  padding: 32px 16px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
}

.history-table {
  border-radius: 8px;
  overflow: hidden;
}

@media (max-width: 768px) {
  .history-project-info {
    grid-template-columns: 1fr;
  }

  .filter-item {
    max-width: none;
  }
}
</style>
